import { defineStore } from 'pinia'
import { reactive } from 'vue'
import type { ICMSLocationFlagFilters } from '@/api/apiGroup/apiCMSLocations'

export type TCMSLocationsScope = 'footer' | 'menu' | 'locations' | 'all'

interface ICMSLocationsScope {
	flagFilters?: ICMSLocationFlagFilters
	onlyActive: boolean
}

const SCOPES: Record<TCMSLocationsScope, ICMSLocationsScope> = {
	footer: { flagFilters: { showInFooter: true, deliveryBus: false, passengerBus: false }, onlyActive: true },
	menu: { flagFilters: { showInMenu: true }, onlyActive: true },
	locations: { flagFilters: { showOnLocations: true }, onlyActive: true },
	all: { onlyActive: false },
}

interface ICMSLocationsStoreState {
	scopedCMSLocations: Record<string, ICMSLocationModel[]>
	scopedCMSLocationsPromises: Record<string, Promise<ICMSLocationModel[]> | null>
}

interface ICMSLocationsStore {
	readonly state: ICMSLocationsStoreState
	fetchCMSLocationsForScope: (scope: TCMSLocationsScope) => Promise<ICMSLocationModel[] | undefined>
}

export const useCMSLocationsStore = defineStore('CMSLocationsStore', (): ICMSLocationsStore => {
	const { locale } = useI18n()
	const _state = reactive<ICMSLocationsStoreState>({
		scopedCMSLocations: {},
		scopedCMSLocationsPromises: {},
	})

	const cacheKeyFor = (scope: TCMSLocationsScope) => `${scope}:${locale.value}`

	const fetchCMSLocationsForScope = async (scope: TCMSLocationsScope) => {
		const cacheKey = cacheKeyFor(scope)

		try {
			const cached = _state.scopedCMSLocations[cacheKey]
			if (cached && cached.length > 0) {
				return cached
			}

			const pending = _state.scopedCMSLocationsPromises[cacheKey]
			if (pending) {
				return pending
			}

			_state.scopedCMSLocationsPromises[cacheKey] = (async () => {
				const { flagFilters, onlyActive } = SCOPES[scope]
				const { data } = await ApiService.fetchEveryCMSLocation(locale.value, { onlyActive, flagFilters, skipSSRPayload: true })

				_state.scopedCMSLocations[cacheKey] = data ?? []

				return _state.scopedCMSLocations[cacheKey]
			})()

			return await _state.scopedCMSLocationsPromises[cacheKey]
		} catch (error) {
			logger.log(error)
		} finally {
			_state.scopedCMSLocationsPromises[cacheKey] = null
		}
	}

	return {
		state: _state,
		fetchCMSLocationsForScope,
	}
})
